<template>
  <div :style="$route.params.user_id ? '' : 'overflow-x: auto;'">
    <b-table
      :fields="getFields"
      :items="getItems"
      bordered
      small
      head-variant="dark"
      class="text-wrap-in-table"
    >
      <template #cell(ce_level_after.status)="data">
        <span>{{ formatCeLevels(data.item.ce_levels_after, data.item.ce_level_after) }}</span>
      </template>

      <template #cell(ce_level_before.status)="data">
        <span>{{ formatCeLevels(data.item.ce_levels_before, data.item.ce_level_before) }}</span>
      </template>

      <template #cell(action)="data">
        <div class="d-flex align-items-center justify-content-center">
          <feather-icon
            icon="EyeIcon"
            class="cursor-pointer mr-50 text-primary"
            title="View Details"
            @click="$router.push({ name: 'personnel-details', params: { id: data.item.id , user_id: data.item.user_id} })"
          />
          <feather-icon
            icon="EditIcon"
            class="cursor-pointer text-warning"
            title="Edit Evaluation"
            @click="openEditModal(data.item)"
          />
        </div>
      </template>

      <template #cell(edit_action)="data">
        <feather-icon
          icon="EditIcon"
          class="cursor-pointer text-warning"
          title="Edit Evaluation"
          @click="openEditModal(data.item)"
        />
      </template>

      <template #cell(requests_evaluation)="data">
        <span v-if="data.item.request_evaluations">
          <feather-icon
            icon="EyeIcon"
            class="cursor-pointer"
            @click="formatRequestsEvaluation(data.item.request_evaluations)"
          />
        </span>
      </template>
    </b-table>

    <!-- Modal to edit completed evaluation -->
    <b-modal
      v-model="showEditModal"
      title="Edit Personnel Evaluation"
      centered
      hide-footer
    >
      <b-form @submit.prevent="saveEditEvaluation">
        <b-form-group label="CE Level Before">
          <v-select
            v-model="editForm.ce_levels_before"
            :options="optionsCertificateStatus"
            :reduce="val => val.value"
            multiple
            placeholder="Select CE Level Before"
            :clearable="true"
          />
        </b-form-group>

        <b-form-group label="CE Level After">
          <v-select
            v-model="editForm.ce_levels_after"
            :options="optionsCertificateStatus"
            :reduce="val => val.value"
            multiple
            placeholder="Select CE Level After"
            :clearable="true"
          />
        </b-form-group>

        <b-form-group label="Evaluator">
          <v-select
            v-model="editForm.evaluator_id"
            :options="optionsUsers"
            placeholder="Select Evaluator"
            :clearable="true"
          />
        </b-form-group>

        <b-form-group label="Conclusion">
          <b-form-input v-model="editForm.conclusion" />
        </b-form-group>

        <b-form-group label="Remarks">
          <b-form-input v-model="editForm.personnel_evaluation_remarks" />
        </b-form-group>

        <b-form-group label="Next Evaluation Date">
          <b-form-input
            v-model="editForm.next_evaluation_date"
            type="date"
          />
        </b-form-group>

        <b-form-group label="Activity">
          <b-form-input v-model="editForm.activity" />
        </b-form-group>

        <div class="text-right mt-2">
          <b-button
            variant="secondary"
            class="mr-1"
            @click="showEditModal = false"
          >
            Cancel
          </b-button>
          <b-button
            variant="primary"
            type="submit"
            :disabled="saving"
          >
            <b-spinner
              v-if="saving"
              small
              type="grow"
            />
            Save Changes
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import questions from './questions'

export default {
  components: {
    vSelect,
  },
  props: {
    entryData: {
      type: [Object, Array],
      default: () => ({}),
    },
  },
  data() {
    return {
      questions,
      showEditModal: false,
      saving: false,
      editForm: {
        id: null,
        ce_levels_before: [],
        ce_levels_after: [],
        evaluator_id: null,
        conclusion: '',
        personnel_evaluation_remarks: '',
        next_evaluation_date: '',
        activity: '',
      },
      statusMap: {
        1: '1 require training',
        2: '2 application review',
        3: '3 conduct evaluation with supervision',
        4: '4 conduct evaluation without supervision',
        5: '5 technical review and certification decision',
        6: '6 conduct audit/inspection',
        7: '7 lead auditor',
        8: 'not applicable',
        9: 'eligible for second review',
        10: 'inspection coordinator',
      },
    }
  },
  computed: {
    optionsCertificateStatus() {
      return this.$store.getters['generalData/certificateStatus/certificateStatus']
    },
    optionsUsers() {
      return this.$store.getters['generalData/engineersList/engineersList']
    },
    getFields() {
      const fields = [
        { key: 'ce_level_after.status', label: 'Evaluation After' },
        { key: 'ce_level_before.status', label: 'Level Before Evaluation' },
        { key: 'conclusion', label: 'Conclusion' },
        { key: 'created_at', label: 'Evaluation Date' },
        { key: 'next_evaluation_date', label: 'Next Evaluation Date' },
        { key: 'product', label: 'Product' },
        { key: 'personnel_evaluation_remarks', label: 'Remarks' },
        { key: 'activity', label: 'Activity' },
        { key: 'request_evaluations_count', label: 'Application reviewed' },
        { key: 'trainings_count', label: 'Trainings' },
        { key: 'audits_count', label: 'Audits observer' },
      ]

      if (!this.$route.params.user_id) {
        fields.push({ key: 'action', label: 'Actions' })
      } else {
        fields.push({ key: 'edit_action', label: 'Edit' })
        fields.push({ key: 'requests_evaluation', label: 'requests evaluation' })
      }

      return fields
    },
    getItems() {
      const formatDate = str => {
        if (!str) return ''
        const date = new Date(str)
        return date.toLocaleDateString('en-GB')
      }

      let data = []

      if (Array.isArray(this.entryData)) {
        data = this.entryData
      } else if (typeof this.entryData === 'object' && this.entryData !== null) {
        data = [this.entryData]
      }

      return data.map(item => ({
        ...item,
        created_at: formatDate(item.completed_at || item.created_at),
        next_evaluation_date: formatDate(item.next_evaluation_date),
      }))
    },
  },
  async mounted() {
    if (!this.$store.getters['generalData/certificateStatus/certificateStatusLoaded']) {
      await this.$store.dispatch('generalData/certificateStatus/fetchCertificateStatus')
    }
    if (!this.$store.getters['generalData/engineersList/engineersLoaded']) {
      await this.$store.dispatch('generalData/engineersList/fetchEngineersList')
    }
  },
  methods: {
    formatCeLevels(levelsArray, levelSingle) {
      if (Array.isArray(levelsArray) && levelsArray.length) {
        return levelsArray.map(id => this.statusMap[id] || id).join(', ')
      }
      if (levelSingle) {
        if (typeof levelSingle === 'object') return levelSingle.status || this.statusMap[levelSingle.id] || levelSingle.id
        return this.statusMap[levelSingle] || levelSingle
      }
      return '-'
    },
    openEditModal(item) {
      this.editForm.id = item.id
      this.editForm.conclusion = item.conclusion || ''
      this.editForm.personnel_evaluation_remarks = item.personnel_evaluation_remarks || ''
      this.editForm.next_evaluation_date = item.next_evaluation_date ? item.next_evaluation_date.slice(0, 10) : ''
      this.editForm.activity = item.activity || ''
      this.editForm.evaluator_id = item.evaluator_id || (item.evaluator ? item.evaluator.id : null)

      if (Array.isArray(item.ce_levels_before) && item.ce_levels_before.length) {
        this.editForm.ce_levels_before = [...item.ce_levels_before]
      } else if (item.ce_level_before) {
        this.editForm.ce_levels_before = [typeof item.ce_level_before === 'object' ? item.ce_level_before.id : item.ce_level_before]
      } else {
        this.editForm.ce_levels_before = []
      }

      if (Array.isArray(item.ce_levels_after) && item.ce_levels_after.length) {
        this.editForm.ce_levels_after = [...item.ce_levels_after]
      } else if (item.ce_level_after) {
        this.editForm.ce_levels_after = [typeof item.ce_level_after === 'object' ? item.ce_level_after.id : item.ce_level_after]
      } else {
        this.editForm.ce_levels_after = []
      }

      this.showEditModal = true
    },
    // v-select hands back the whole option object; the API wants the plain id.
    unwrapId(value) {
      if (typeof value !== 'object' || value === null) return value
      return value.value !== undefined ? value.value : value.id
    },

    saveEditEvaluation() {
      this.saving = true
      const evalId = this.unwrapId(this.editForm.evaluator_id)

      const payload = {
        ce_levels_before: this.editForm.ce_levels_before,
        ce_level_before: this.editForm.ce_levels_before.length ? Math.max(...this.editForm.ce_levels_before) : null,
        ce_levels_after: this.editForm.ce_levels_after,
        ce_level_after: this.editForm.ce_levels_after.length ? Math.max(...this.editForm.ce_levels_after) : null,
        evaluator_id: evalId,
        conclusion: this.editForm.conclusion,
        personnel_evaluation_remarks: this.editForm.personnel_evaluation_remarks,
        next_evaluation_date: this.editForm.next_evaluation_date,
        activity: this.editForm.activity,
      }

      axios.put(`/personnel-evaluation/${this.editForm.id}`, payload)
        .then(res => {
          if (res.status === 200 || res.status === 201) {
            this.showEditModal = false
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Personnel evaluation updated successfully',
                icon: 'CheckIcon',
                variant: 'success',
              },
            })
            window.location.reload()
          }
        })
        .catch(err => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: err.response?.data?.message || 'Failed to update evaluation',
              icon: 'BellIcon',
              variant: 'error',
            },
          })
        })
        .finally(() => {
          this.saving = false
        })
    },
    formatRequestsEvaluation(requestDetails) {
      this.$emit('show-requests-evaluation', requestDetails)
    },
  },
}
</script>
